import React, { useRef } from 'react'
import clsx from 'clsx'
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form'

interface FormProps {
    className?: string
    children?: React.ReactNode
    endpoint?: string
    isFormData?: boolean
    onSuccess?: (data: any) => void
    onError?: (error: any) => void
    popupSuccessContent?: any
    popupErrorContent?: any
    clearOnSuccess?: boolean
}

interface FormValues {
    [key: string]: any
}

export default function Form({
    className,
    children,
    endpoint,
    isFormData,
    onSuccess,
    onError,
    clearOnSuccess
}: FormProps){

    const form = useRef<HTMLFormElement>(null)

    // form validations
    const methods = useForm<FormValues>({
        criteriaMode: 'all',
        mode: 'all'
    })

    // submit function
    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        // add a class called 'is-sending' to the form while it's processing the request
        if (form.current) {
            form.current.classList.add('is-sending')
            document.dispatchEvent(new Event('formSending'))
        }

        let body
    
        // usable only if the form contain upload fields
        if (isFormData) {
            const formData = new FormData()

            Object.keys(data).forEach(key => {
                const value = data[key]
                formData.append(key, value)
            })

            // add files
            if (form.current) {
                Array.from(form.current.elements).forEach(element => {
                    if (element instanceof HTMLInputElement && element.type === 'file' && element.files?.length) {
                        Array.from(element.files).forEach((file, index) => {
                            formData.append(`${element.name}[${index}]`, file)
                        })
                    }
                })
            }

            body = formData
        } else {
            body = JSON.stringify(data)
        }

        // connect to the API endpoint to send the email
        fetch(endpoint, {
            method: 'post',
            body: body
        })

        .then(response => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Failed to send message.')
            }
        })

        // if success
        .then((responseData) => {
            if (onSuccess) {
                setTimeout(() => {
                    onSuccess(responseData)

                    if (form.current) {
                        form.current.classList.remove('is-sending')
                        document.dispatchEvent(new Event('formSent'))

                        setTimeout(() => {
                            document.dispatchEvent(new Event('clearFormSent'))
                        }, 4000)

                        if (clearOnSuccess) {
                            form.current.reset()
                            document.dispatchEvent(new Event('resetForm'))
                        }
                    }
                }, 1000)
            }
        })

        // if error
        .catch(error => {
            console.error('Error:', error)

            if (onError) {
                setTimeout(() => {
                    onError(error)

                    if (form.current) {
                        form.current.classList.remove('is-sending')
                        document.dispatchEvent(new Event('formResetSubmit'))
                        document.dispatchEvent(new Event('formError'))
                    }
                }, 1000)
            }
            
        })
    }

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className={clsx('global-form', className)}
                ref={form}
            >
                {children}   
            </form>
            
        </FormProvider>
    )
}